package wat.service

import kotlinx.browser.window
import kotlinx.coroutines.await
import wat.Supa
import wat.model.*
import wat.ui.DebugLog
import kotlin.js.Promise

object DataService {
    var sports = mutableListOf<Sport>()
    var categories = mutableListOf<Category>()
    var units = mutableListOf<MeasureUnit>()
    var bmiZones = mutableListOf<BmiZone>()
    var entries = mutableListOf<Entry>()
    var diaryEntries = mutableListOf<DiaryEntry>()
    var profile = Profile()
    var sportDefs = mutableMapOf<String, SportDef>()

    // Calorie config
    var calAgeBaseline = 20.0
    var calAgeFactor = 0.002
    var calFemaleFactor = 0.85
    var calDefaultMet = 5.0

    private fun logResult(table: String, res: dynamic): Boolean {
        val error = res.error
        if (error != null && error != undefined) {
            DebugLog.error("$table: ${error.message} (code=${error.code}, details=${error.details}, hint=${error.hint})")
            return false
        }
        val data = res.data
        if (data == null || data == undefined) {
            DebugLog.warn("$table: data is null/undefined (no error)")
            return false
        }
        val len = try { (data as Array<dynamic>).size } catch (_: Exception) { -1 }
        DebugLog.db("$table: OK, ${if (len >= 0) "$len rows" else "single row"}")
        return true
    }

    // Map unit ID (UUID) -> unit key ("min", "km", etc.)
    private var unitIdToKey = mutableMapOf<String, String>()

    suspend fun loadActivities(): Boolean {
        return try {
            DebugLog.log("  Loading activity_types...")
            // Build unit ID->key map from already-loaded units
            unitIdToKey.clear()
            units.forEach { u ->
                val id = u.id?.toString() ?: ""
                if (id.isNotBlank()) unitIdToKey[id] = u.key
            }
            DebugLog.log("  Unit ID map: ${unitIdToKey.size} entries (${unitIdToKey.values.take(5).joinToString(",")})")

            val res = Supa.select("activity_types", "*, activity_categories(key)")
            if (!logResult("activity_types", res)) return false
            val data = res.data
            sports.clear()
            val arr = data as Array<dynamic>
            arr.forEach { a ->
                // Resolve unit_id UUID to unit key
                val rawUnitId = a.unit_id?.toString() ?: ""
                val unitStr = unitIdToKey[rawUnitId] ?: rawUnitId.ifBlank { "min" }
                var availUnits = listOf(unitStr)
                try {
                    val au = a.available_units
                    if (au != null) {
                        val parsed = if (au is String) JSON.parse<Array<String>>(au) else au as Array<String>
                        if (parsed.isNotEmpty()) availUnits = parsed.toList()
                    }
                } catch (_: Exception) {}
                val nameSk = a.name_sk?.toString() ?: ""
                sports.add(Sport(
                    k = a.key?.toString() ?: "",
                    i = a.icon?.toString() ?: "",
                    l = nameSk,
                    lEn = a.name_en?.toString() ?: "",
                    lCz = a.name_cz?.toString() ?: "",
                    lDe = a.name_de?.toString() ?: "",
                    u = unitStr,
                    cat = a.activity_categories?.key?.toString() ?: "",
                    dm = (a.default_duration as? Number)?.toInt() ?: 0,
                    dv = (a.default_value as? Number)?.toDouble() ?: 0.0,
                    met = (a.met as? Number)?.toDouble() ?: 3.0,
                    color = a.color?.toString() ?: "",
                    isDefault = a.is_default == true,
                    isCustom = a.is_custom == true,
                    activityId = a.id,
                    availableUnits = availUnits
                ))
            }
            // Assign colors to sports without one (deterministic per category)
            assignMissingColors()
            DebugLog.ok("  Sports parsed: ${sports.size} (defaults: ${sports.count { it.isDefault }})")
            true
        } catch (e: Exception) {
            DebugLog.error("activity_types EXCEPTION: ${e.message}")
            DebugLog.error("  Stack: ${e.stackTraceToString().take(300)}")
            false
        }
    }

    suspend fun loadCategories(): Boolean {
        return try {
            DebugLog.log("  Loading activity_categories...")
            val res = Supa.select("activity_categories")
            if (!logResult("activity_categories", res)) return false
            val data = res.data
            categories.clear()
            val arr = data as Array<dynamic>
            arr.forEach { c ->
                categories.add(Category(id = c.id, key = c.key?.toString() ?: ""))
            }
            true
        } catch (e: Exception) {
            DebugLog.error("activity_categories EXCEPTION: ${e.message}")
            false
        }
    }

    suspend fun loadUnits(): Boolean {
        return try {
            DebugLog.log("  Loading units...")
            val res = Supa.select("units")
            if (!logResult("units", res)) return false
            val data = res.data
            units.clear()
            val arr = data as Array<dynamic>
            arr.forEach { u ->
                units.add(MeasureUnit(
                    id = u.id,
                    key = u.key?.toString() ?: "",
                    step = (u.step_size as? Number)?.toDouble() ?: 1.0,
                    threshold = (u.step_threshold as? Number)?.toDouble() ?: 0.0,
                    stepAbove = (u.step_size_above as? Number)?.toDouble() ?: 0.0,
                    decimals = (u.decimal_places as? Number)?.toInt() ?: 0
                ))
            }
            true
        } catch (e: Exception) {
            DebugLog.error("units EXCEPTION: ${e.message}")
            false
        }
    }

    suspend fun loadBmiZones(): Boolean {
        return try {
            DebugLog.log("  Loading bmi_zones...")
            val res = Supa.selectOrdered("bmi_zones", "*", "min_bmi", ascending = true)
            if (!logResult("bmi_zones", res)) return false
            val data = res.data
            bmiZones.clear()
            val arr = data as Array<dynamic>
            arr.forEach { z ->
                bmiZones.add(BmiZone(
                    minBmi = (z.min_bmi as? Number)?.toDouble() ?: 0.0,
                    maxBmi = (z.max_bmi as? Number)?.toDouble() ?: 100.0,
                    color = z.color?.toString() ?: "#fff",
                    translationKey = z.zone_translation_key?.toString() ?: ""
                ))
            }
            true
        } catch (e: Exception) {
            DebugLog.error("bmi_zones EXCEPTION: ${e.message}")
            false
        }
    }

    suspend fun loadAppConfig(): Boolean {
        return try {
            DebugLog.log("  Loading app_config...")
            val res = Supa.select("app_config")
            if (!logResult("app_config", res)) return false
            val data = res.data
            val arr = data as Array<dynamic>
            arr.forEach { c ->
                val key = c.key?.toString() ?: ""
                val value = (c.value as? Number)?.toDouble() ?: 0.0
                when (key) {
                    "cal_age_baseline" -> calAgeBaseline = value
                    "cal_age_factor" -> calAgeFactor = value
                    "cal_female_factor" -> calFemaleFactor = value
                    "cal_default_met" -> calDefaultMet = value
                }
            }
            true
        } catch (e: Exception) {
            DebugLog.error("app_config EXCEPTION: ${e.message}")
            false
        }
    }

    suspend fun loadProfile(userId: String): Boolean {
        return try {
            DebugLog.log("  Loading profile for $userId...")
            val res = Supa.selectSingle("profiles", "id", userId)
            val error = res.error
            if (error != null && error != undefined) {
                DebugLog.error("profiles: ${error.message} (code=${error.code})")
                DebugLog.warn("  Trying localStorage fallback...")
                loadProfileFromLocal()
                return true
            }
            val data = res.data
            if (data != null && data != undefined) {
                // Load DB fields (birthdate & defaultPage are NOT in DB — stored in localStorage only)
                val dbProfile = Profile(
                    height = (data.height as? Number)?.toInt() ?: 0,
                    sex = data.sex?.toString() ?: "m",
                    goalWeight = (data.goal_weight as? Number)?.toDouble() ?: 0.0,
                    birthdate = "",
                    lang = data.lang?.toString() ?: "sk",
                    defaultPage = "train"
                )
                DebugLog.db("profiles DB: h=${dbProfile.height}, sex=${dbProfile.sex}, gw=${dbProfile.goalWeight}, lang=${dbProfile.lang}")

                // Merge with localStorage for fields not in DB
                profile = dbProfile
                loadProfileFromLocal() // merges birthdate, defaultPage from localStorage
                // Keep DB values for fields that ARE in DB
                profile = profile.copy(
                    height = if (dbProfile.height > 0) dbProfile.height else profile.height,
                    sex = dbProfile.sex,
                    goalWeight = dbProfile.goalWeight,
                    lang = dbProfile.lang
                )
                DebugLog.db("profiles merged: h=${profile.height}, sex=${profile.sex}, gw=${profile.goalWeight}, bd=${profile.birthdate}, dp=${profile.defaultPage}")
                saveProfileToLocal(profile)
                true
            } else {
                DebugLog.warn("profiles: data is null — trying localStorage")
                loadProfileFromLocal()
                true
            }
        } catch (e: Exception) {
            DebugLog.error("profiles EXCEPTION: ${e.message}")
            loadProfileFromLocal()
            true
        }
    }

    suspend fun loadEntries(userId: String): Boolean {
        return try {
            DebugLog.log("  Loading entries for $userId...")
            val res = Supa.selectWhereOrdered("entries", "user_id", userId, "*", "date", ascending = false)
            if (!logResult("entries", res)) return false
            val data = res.data
            entries.clear()
            val arr = data as Array<dynamic>
            arr.forEach { e ->
                val sportsList = mutableListOf<SportEntry>()
                val sp = e.sports
                if (sp != null) {
                    try {
                        val spArr = if (sp is String) JSON.parse<Array<dynamic>>(sp) else sp as Array<dynamic>
                        // Known unit keys for validation
                        val knownUnits = units.map { it.key }.toSet()

                        spArr.forEach { s ->
                            val rawMode = s.mode?.toString() ?: ""
                            // Try to resolve: UUID → key, or check if it's a known unit
                            val resolved = unitIdToKey[rawMode] ?: rawMode
                            val sportDef = sports.find { it.k == s.k?.toString() }
                            // Use resolved mode only if it's a known unit key, otherwise use sport default
                            val finalMode = if (resolved.isNotBlank() && resolved in knownUnits) resolved
                                            else sportDef?.u ?: "min"
                            sportsList.add(SportEntry(
                                k = s.k?.toString() ?: "",
                                mode = finalMode,
                                `val` = (s.`val` ?: s.min ?: 0) as? Double ?: ((s.`val` ?: s.min ?: 0) as? Number)?.toDouble() ?: 0.0
                            ))
                        }
                    } catch (_: Exception) {}
                }
                entries.add(Entry(
                    id = e.id,
                    userId = userId,
                    date = e.date?.toString() ?: "",
                    weight = (e.weight as? Number)?.toDouble() ?: 0.0,
                    sports = sportsList,
                    note = e.note?.toString() ?: ""
                ))
            }
            DebugLog.ok("  Entries parsed: ${entries.size} (with weight: ${entries.count { it.weight > 0 }})")
            true
        } catch (e: Exception) {
            DebugLog.error("entries EXCEPTION: ${e.message}")
            DebugLog.error("  Stack: ${e.stackTraceToString().take(300)}")
            false
        }
    }

    suspend fun loadDiaryEntries(userId: String): Boolean {
        return try {
            DebugLog.log("  Loading diary_entries for $userId...")
            val res = Supa.selectWhereOrdered("diary_entries", "user_id", userId, "*", "date", ascending = false)
            if (!logResult("diary_entries", res)) return false
            val data = res.data
            diaryEntries.clear()
            val arr = data as Array<dynamic>
            arr.forEach { e ->
                val tagsList = mutableListOf<String>()
                val tags = e.tags
                if (tags != null) {
                    try {
                        val tArr = if (tags is String) JSON.parse<Array<String>>(tags) else tags as Array<String>
                        tagsList.addAll(tArr)
                    } catch (_: Exception) {}
                }
                diaryEntries.add(DiaryEntry(
                    id = e.id,
                    userId = userId,
                    date = e.date?.toString() ?: "",
                    body = e.body?.toString() ?: "",
                    mood = (e.mood as? Number)?.toInt(),
                    tags = tagsList
                ))
            }
            DebugLog.ok("  Diary parsed: ${diaryEntries.size}")
            true
        } catch (e: Exception) {
            DebugLog.error("diary_entries EXCEPTION: ${e.message}")
            false
        }
    }

    suspend fun saveEntry(userId: String, date: String, weight: Double, sports: List<SportEntry>, note: String, entryId: dynamic = null): Boolean {
        return try {
            val sportsArr = js("[]")
            sports.forEach { s ->
                val obj = js("{}")
                obj.k = s.k
                obj.mode = s.mode
                obj.`val` = s.`val`
                sportsArr.push(obj)
            }
            val data = js("{}")
            data.user_id = userId
            data.date = date
            data.weight = if (weight > 0) weight else null
            data.sports = sportsArr
            data.note = note

            // Always try to find existing entry for this date to avoid duplicates
            val existingId = entryId ?: entries.find { it.date == date }?.id

            if (existingId != null && existingId != undefined) {
                // Update existing entry
                val updateData = js("{}")
                updateData.weight = data.weight
                updateData.sports = data.sports
                updateData.note = data.note
                val res = Supa.update("entries", updateData, "id", existingId)
                val error = res.error
                if (error != null && error != undefined) {
                    DebugLog.error("saveEntry update error: ${error.message}")
                    return false
                }
                DebugLog.ok("saveEntry: updated id=$existingId")

                // Delete any other duplicate entries for this date
                val dupes = entries.filter { it.date == date && it.id != existingId }
                dupes.forEach { dupe ->
                    Supa.delete("entries", "id", dupe.id)
                    DebugLog.db("saveEntry: deleted duplicate id=${dupe.id}")
                }
                true
            } else {
                // No existing entry — insert new
                val res = Supa.insert("entries", data)
                val error = res.error
                if (error != null && error != undefined) {
                    DebugLog.error("saveEntry insert error: ${error.message}")
                    return false
                }
                DebugLog.ok("saveEntry: inserted new for date=$date")
                true
            }
        } catch (e: Exception) {
            DebugLog.error("saveEntry EXCEPTION: ${e.message}")
            false
        }
    }

    // Clean up all duplicate entries per date for a user
    suspend fun cleanupDuplicates(userId: String): Int {
        var deleted = 0
        try {
            val byDate = entries.groupBy { it.date }
            byDate.forEach { (_, list) ->
                if (list.size > 1) {
                    // Keep the FIRST one (newest, since entries are sorted desc) — NO merging
                    val keep = list.first()
                    // Delete ALL others
                    list.drop(1).forEach { dupe ->
                        Supa.delete("entries", "id", dupe.id)
                        deleted++
                    }
                    DebugLog.db("cleanup ${keep.date}: kept id=${keep.id}, deleted ${list.size - 1} dupes")
                }
            }
            // Fix entries with duplicate sport entries from bad merge
            // Count how many times each sport appears; if same sport+val appears
            // multiple times it's a merge artifact, not intentional
            entries.forEach { entry ->
                if (entry.sports.size <= 1) return@forEach
                // Find exact duplicates (same k + same val)
                val unique = mutableListOf<SportEntry>()
                val seen = mutableSetOf<String>()
                entry.sports.forEach { se ->
                    val fingerprint = "${se.k}|${se.`val`}|${se.mode}"
                    if (fingerprint !in seen) {
                        seen.add(fingerprint)
                        unique.add(se)
                    }
                }
                if (unique.size < entry.sports.size) {
                    DebugLog.warn("Entry ${entry.date}: ${entry.sports.size} sports → ${unique.size} after removing exact duplicates")
                    val sportsArr = js("[]")
                    unique.forEach { s ->
                        val obj = js("{}")
                        obj.k = s.k; obj.mode = s.mode; obj.`val` = s.`val`
                        sportsArr.push(obj)
                    }
                    val fixData = js("{}")
                    fixData.sports = sportsArr
                    Supa.update("entries", fixData, "id", entry.id)
                }
            }

            if (deleted > 0) {
                DebugLog.ok("cleanupDuplicates: removed $deleted duplicate entries")
            }
            // Always reload after cleanup
            loadEntries(userId)
        } catch (e: Exception) {
            DebugLog.error("cleanupDuplicates EXCEPTION: ${e.message}")
        }
        return deleted
    }

    // Always save profile to localStorage as backup
    fun saveProfileToLocal(p: Profile) {
        try {
            val obj = js("{}")
            obj.height = p.height
            obj.sex = p.sex
            obj.goalWeight = p.goalWeight
            obj.birthdate = p.birthdate
            obj.lang = p.lang
            obj.defaultPage = p.defaultPage
            window.localStorage.setItem("wat_profile", JSON.stringify(obj))
        } catch (_: Exception) {}
    }

    fun loadProfileFromLocal(): Boolean {
        try {
            val raw = window.localStorage.getItem("wat_profile") ?: return false
            val obj = JSON.parse<dynamic>(raw)
            profile = Profile(
                height = (obj.height as? Number)?.toInt() ?: profile.height,
                sex = obj.sex?.toString() ?: profile.sex,
                goalWeight = (obj.goalWeight as? Number)?.toDouble() ?: profile.goalWeight,
                birthdate = obj.birthdate?.toString() ?: profile.birthdate,
                lang = obj.lang?.toString() ?: profile.lang,
                defaultPage = obj.defaultPage?.toString() ?: profile.defaultPage
            )
            DebugLog.ok("Profile loaded from localStorage: h=${profile.height}, bd=${profile.birthdate}")
            return true
        } catch (_: Exception) { return false }
    }

    suspend fun saveProfile(userId: String, p: Profile): Boolean {
        // Always save to localStorage immediately
        saveProfileToLocal(p)

        return try {
            DebugLog.db("saveProfile: id=$userId, h=${p.height}, sex=${p.sex}, gw=${p.goalWeight}, lang=${p.lang}, bd=${p.birthdate}, dp=${p.defaultPage}")

            // Only send columns that exist in DB (birthdate, default_page do NOT exist)
            fun buildDbData(withId: Boolean): dynamic {
                val d = js("{}")
                if (withId) d.id = userId
                d.height = p.height
                d.sex = p.sex
                d.goal_weight = if (p.goalWeight > 0) p.goalWeight else null
                d.lang = p.lang
                d.updated_at = js("new Date().toISOString()")
                return d
            }

            // Strategy 1: upsert
            val res1 = Supa.upsert("profiles", buildDbData(true))
            val err1 = res1.error
            if (err1 != null && err1 != undefined) {
                DebugLog.warn("saveProfile upsert error: ${err1.message} — trying update...")

                // Strategy 2: update without id in data
                val res2 = Supa.update("profiles", buildDbData(false), "id", userId)
                val err2 = res2.error
                if (err2 != null && err2 != undefined) {
                    DebugLog.error("saveProfile update ALSO failed: ${err2.message}")
                    return false
                }
                val count2 = try { (res2.data as? Array<dynamic>)?.size ?: 0 } catch (_: Exception) { 0 }
                DebugLog.ok("saveProfile OK (via update, $count2 rows)")
                return true
            }

            val count1 = try { (res1.data as? Array<dynamic>)?.size ?: 0 } catch (_: Exception) { 0 }
            DebugLog.ok("saveProfile OK (via upsert, $count1 rows)")
            true
        } catch (e: Exception) {
            DebugLog.error("saveProfile EXCEPTION: ${e.message}")
            DebugLog.error("  Stack: ${e.stackTraceToString().take(300)}")
            false
        }
    }

    suspend fun saveDiaryEntry(userId: String, entry: DiaryEntry): Boolean {
        return try {
            val data = js("{}")
            data.user_id = userId
            data.date = entry.date
            data.body = entry.body
            data.mood = entry.mood
            data.tags = entry.tags.toTypedArray()
            val opts = js("{}")
            opts.onConflict = "user_id,date"
            val res = Supa.upsert("diary_entries", data)
            res.error == null || res.error == undefined
        } catch (e: Exception) {
            DebugLog.error("saveDiaryEntry EXCEPTION: ${e.message}")
            false
        }
    }

    suspend fun deleteEntry(entryId: dynamic): Boolean {
        return try {
            val res = Supa.delete("entries", "id", entryId)
            res.error == null || res.error == undefined
        } catch (e: Exception) {
            DebugLog.error("deleteEntry EXCEPTION: ${e.message}")
            false
        }
    }

    suspend fun deleteDiaryEntry(entryId: dynamic): Boolean {
        return try {
            val res = Supa.delete("diary_entries", "id", entryId)
            res.error == null || res.error == undefined
        } catch (e: Exception) {
            DebugLog.error("deleteDiaryEntry EXCEPTION: ${e.message}")
            false
        }
    }

    // Calorie estimation
    fun estimateCalories(sportKey: String, minutes: Double, weight: Double = 85.0, age: Int = 30, sex: String = "m"): Int {
        val sport = sports.find { it.k == sportKey }
        val met = sport?.met ?: calDefaultMet
        val base = met * weight * (minutes / 60.0)
        val ageFac = 1.0 - maxOf(0.0, (age - calAgeBaseline)) * calAgeFactor
        val sexFac = if (sex == "f") calFemaleFactor else 1.0
        return (base * ageFac * sexFac).toInt()
    }

    // Category color palettes - vivid, distinct hues per category
    private val catPalettes = mapOf(
        "kardio" to listOf("#f472b6", "#fb923c", "#ef4444", "#f87171", "#ec4899", "#f43f5e", "#e11d48", "#d946ef", "#f97316", "#fb7185", "#06b6d4", "#86efac", "#4ade80"),
        "sila" to listOf("#a78bfa", "#e879f9", "#fbbf24", "#38bdf8", "#c084fc", "#818cf8", "#a855f7", "#8b5cf6", "#7c3aed", "#9333ea", "#6d28d9", "#d8b4fe", "#b45fe0"),
        "sport" to listOf("#22c55e", "#34d399", "#10b981", "#f97316", "#facc15", "#3b82f6", "#ef4444", "#84cc16", "#f0abfc", "#65a30d", "#60a5fa", "#22d3ee", "#a3e635", "#2dd4bf", "#fb923c", "#dc2626", "#0ea5e9", "#6366f1", "#f472b6", "#0891b2", "#16a34a", "#fbbf24", "#67e8f9", "#4ade80", "#f59e0b", "#93c5fd", "#ca8a04", "#bae6fd"),
        "wellness" to listOf("#67e8f9", "#a5b4fc", "#5eead4", "#99f6e4", "#c4b5fd", "#f97316", "#22d3ee", "#fda4af", "#86efac", "#a7f3d0", "#7dd3fc"),
        "ostatne" to listOf("#60a5fa", "#3b82f6", "#94a3b8", "#818cf8", "#a78bfa", "#fb923c", "#4ade80", "#e879f9", "#d946ef", "#c084fc", "#f472b6", "#fbbf24", "#f87171", "#67e8f9", "#0ea5e9", "#22c55e", "#2dd4bf", "#a3e635", "#38bdf8", "#6366f1", "#34d399", "#f43f5e", "#facc15", "#fb7185", "#f0abfc", "#ec4899", "#78716c"),
        "zlozvyky" to listOf("#f59e0b", "#fbbf24", "#ef4444", "#f97316", "#92400e", "#fb923c", "#facc15", "#d97706", "#84cc16", "#6366f1", "#4338ca", "#b91c1c", "#78716c", "#65a30d", "#dc2626", "#818cf8")
    )
    private val defaultPalette = listOf("#2dd4bf", "#818cf8", "#f472b6", "#fbbf24", "#fb923c", "#ef4444", "#22c55e", "#60a5fa", "#a78bfa", "#34d399")

    private fun assignMissingColors() {
        val byCat = sports.groupBy { it.cat }
        val updated = mutableListOf<Sport>()
        byCat.forEach { (cat, catSports) ->
            val palette = catPalettes[cat] ?: defaultPalette
            catSports.forEachIndexed { idx, sp ->
                if (sp.color.isBlank()) {
                    val color = palette[idx % palette.size]
                    updated.add(sp.copy(color = color))
                }
            }
        }
        // Replace sports with assigned colors
        updated.forEach { u ->
            val idx = sports.indexOfFirst { it.k == u.k }
            if (idx >= 0) sports[idx] = u
        }
    }

    fun getSportColor(key: String): String {
        val sport = sports.find { it.k == key }
        val raw = sport?.color?.ifEmpty { "#2dd4bf" } ?: "#2dd4bf"
        return wat.ui.Styles.adaptColor(raw)
    }

    fun getSportIcon(key: String): String = sports.find { it.k == key }?.i ?: ""
    fun getSportName(key: String): String = sports.find { it.k == key }?.localName() ?: key

    // ── SportDefs persistence via localStorage ──
    fun saveSportDefs() {
        try {
            val obj = js("{}")
            sportDefs.forEach { (k, d) ->
                val sd = js("{}")
                sd.on = d.on
                sd.mode = d.mode
                sd.min = d.min
                sd.`val` = d.`val`
                sd.gw = d.gw
                obj[k] = sd
            }
            window.localStorage.setItem("wat_sport_defs", JSON.stringify(obj))
            DebugLog.db("sportDefs saved to localStorage (${sportDefs.size} entries)")
        } catch (e: Exception) {
            DebugLog.error("saveSportDefs: ${e.message}")
        }
    }

    fun loadSportDefs() {
        try {
            val raw = window.localStorage.getItem("wat_sport_defs") ?: return
            val obj = JSON.parse<dynamic>(raw)
            val keys = js("Object.keys(obj)") as Array<String>
            keys.forEach { k ->
                val d = obj[k]
                sportDefs[k] = SportDef(
                    on = d.on as? Boolean ?: true,
                    mode = d.mode?.toString() ?: "",
                    min = (d.min as? Number)?.toInt() ?: 0,
                    `val` = (d.`val` as? Number)?.toDouble() ?: 0.0,
                    gw = (d.gw as? Number)?.toDouble() ?: 0.0
                )
            }
            DebugLog.ok("sportDefs loaded from localStorage (${sportDefs.size} entries)")
        } catch (e: Exception) {
            DebugLog.warn("loadSportDefs: ${e.message}")
        }
    }

    // Load all data
    suspend fun loadAll(userId: String): Boolean {
        DebugLog.log("=== loadAll START ===")
        var ok = true
        var failed = mutableListOf<String>()

        if (!loadCategories()) { ok = false; failed.add("categories") }
        if (!loadUnits()) { ok = false; failed.add("units") }
        if (!loadActivities()) { ok = false; failed.add("activities") }
        if (!loadBmiZones()) { ok = false; failed.add("bmi_zones") }
        if (!loadAppConfig()) { ok = false; failed.add("app_config") }
        if (!loadProfile(userId)) { ok = false; failed.add("profile") }
        if (!loadEntries(userId)) { ok = false; failed.add("entries") }
        if (!loadDiaryEntries(userId)) { ok = false; failed.add("diary") }

        // Clean up duplicate entries per date
        cleanupDuplicates(userId)

        // Load sportDefs from localStorage after sports are loaded
        loadSportDefs()

        if (ok) {
            DebugLog.ok("=== loadAll COMPLETE: all OK ===")
        } else {
            DebugLog.error("=== loadAll COMPLETE: FAILED tables: ${failed.joinToString(", ")} ===")
        }

        DebugLog.log("Summary: ${sports.size} sports, ${categories.size} cats, ${units.size} units, ${bmiZones.size} bmi, ${entries.size} entries, ${diaryEntries.size} diary")
        return ok
    }
}
